// src/subscreens/mechanicadmin/reviews/MechanicReviews.jsx

import React, { useState, useEffect, useContext } from "react";
import { Row, Col } from "react-bootstrap";
import { FaStar, FaRegStar, FaStarHalfAlt, FaWrench, FaQuoteLeft } from "react-icons/fa";
import { FiThumbsUp, FiMessageSquare, FiAward } from "react-icons/fi";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import MechanicAdminLayout from "../../../layout/mechanic/MechanicMasterLayout";
import "../../../assets/css/mechanic/reviewsrating/ReviewsRating.css";

/* ── Render Stars ── */
const RenderStars = ({ rating, size = 16 }) => {
  const stars = [];
  const full  = Math.floor(rating);
  const half  = rating % 1 !== 0;
  for (let i = 0; i < full; i++)
    stars.push(<FaStar key={`f${i}`} style={{ color: "#FDB714", fontSize: size }} />);
  if (half)
    stars.push(<FaStarHalfAlt key="h" style={{ color: "#FDB714", fontSize: size }} />);
  for (let i = 0; i < 5 - Math.ceil(rating); i++)
    stars.push(<FaRegStar key={`e${i}`} style={{ color: "#FDB714", fontSize: size }} />);
  return <span className="mr-stars-row">{stars}</span>;
};

/* ── Rating Bar ── */
const RatingBar = ({ star, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="mr-rating-bar-row">
      <span className="mr-bar-label">{star} ★</span>
      <div className="mr-bar-track">
        <div className="mr-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="mr-bar-pct">{pct}%</span>
      <span className="mr-bar-count">({count})</span>
    </div>
  );
};

export default function MechanicReviews() {
  const { baseUrl, loggedInMechanic , authToken} = useContext(AppContext);

  const [reviewsData,  setReviewsData]  = useState([]);
  const [filterRating, setFilterRating] = useState("all");
  const [helpfulMap,   setHelpfulMap]   = useState({});
  const [loading,      setLoading]      = useState(true);

  // ── FETCH ──────────────────────────────────────────────────────
  const fetchMechanicReviews = () => {
    if (!loggedInMechanic) return;
    setLoading(true);

    const formData = new FormData();
    formData.append("srmechanicadmin_id", loggedInMechanic.id);

    axios.post(`${baseUrl}/mechanicadmin/reviews/getMechanicReviews`, formData, {
      headers: { "Content-Type": "multipart/form-data" ,
        Authorization: `Bearer ${authToken}`
      },
    }).then((res) => {
      if (res.data.success) setReviewsData(res.data.reviews || []);
      else setReviewsData([]);
      setLoading(false);
    }).catch(() => {
      setReviewsData([]);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (loggedInMechanic) fetchMechanicReviews();
  }, [loggedInMechanic]);

  // ── COMPUTED STATS ──────────────────────────────────────────────
  const totalReviews = reviewsData.length;
  const avgRating    = totalReviews > 0
    ? (reviewsData.reduce((a, r) => a + r.rating, 0) / totalReviews).toFixed(1)
    : "0.0";
  const fiveStars  = reviewsData.filter(r => r.rating === 5).length;
  const fourStars  = reviewsData.filter(r => r.rating === 4).length;
  const threeStars = reviewsData.filter(r => r.rating === 3).length;
  const twoStars   = reviewsData.filter(r => r.rating === 2).length;
  const oneStar    = reviewsData.filter(r => r.rating === 1).length;
  const totalHelpful = reviewsData.reduce((a, r) => a + (r.helpful || 0), 0);

  const statsData = [
    { icon: <FaStar />,          value: avgRating,    label: "Average Rating", sub: "Out of 5.0"  },
    { icon: <FiMessageSquare />, value: totalReviews, label: "Total Reviews",  sub: "All time"    },
    { icon: <FiThumbsUp />,      value: totalHelpful, label: "Helpful Votes",  sub: "Received"    },
    { icon: <FiAward />,         value: fiveStars,    label: "5-Star Reviews", sub: totalReviews > 0 ? `${Math.round((fiveStars / totalReviews) * 100)}% of total` : "0%" },
  ];

  // ── FILTER ──────────────────────────────────────────────────────
  const filtered = filterRating === "all"
    ? reviewsData
    : reviewsData.filter(r => r.rating === parseInt(filterRating));

  const handleHelpful = (id, current) => {
    setHelpfulMap(prev => ({ ...prev, [id]: prev[id] ? prev[id] : (current || 0) + 1 }));
  };

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <MechanicAdminLayout>
      <div className="mr-page">

        {/* PAGE HEADER */}
        <div className="mr-page-header">
          <h2 className="mr-page-title">Reviews & Ratings</h2>
          <p className="mr-page-sub">What customers say about your service</p>
        </div>

        {/* STAT CARDS */}
        <Row className="g-4 mb-4">
          {statsData.map((s, i) => (
            <Col key={i} xl={3} lg={3} md={6} sm={12}>
              <div className="mr-stat-card">
                <div className="mr-stat-icon">{s.icon}</div>
                <div className="mr-stat-content">
                  <h3>{s.value}</h3>
                  <p>{s.label}</p>
                  <span className="mr-stat-sub">{s.sub}</span>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* RATING BREAKDOWN */}
        <div className="mr-breakdown-card">
          <div className="mr-breakdown-top">
            <div className="mr-breakdown-left">
              <span className="mr-big-rating">{avgRating}</span>
              <RenderStars rating={parseFloat(avgRating)} size={24} />
              <p className="mr-big-sub">{totalReviews} reviews total</p>
            </div>
            <div className="mr-breakdown-bars">
              <RatingBar star={5} count={fiveStars}  total={totalReviews} />
              <RatingBar star={4} count={fourStars}  total={totalReviews} />
              <RatingBar star={3} count={threeStars} total={totalReviews} />
              <RatingBar star={2} count={twoStars}   total={totalReviews} />
              <RatingBar star={1} count={oneStar}    total={totalReviews} />
            </div>
          </div>

          <div className="mr-breakdown-divider" />

          <div className="mr-breakdown-filter">
            <span className="mr-filter-label">Filter by Rating:</span>
            <div className="mr-filter-chips">
              {["all", "5", "4", "3", "2", "1"].map(r => (
                <button
                  key={r}
                  className={`mr-filter-chip ${filterRating === r ? "active" : ""}`}
                  onClick={() => setFilterRating(r)}
                >
                  {r === "all" ? "All Reviews" : `${r} ★`}
                </button>
              ))}
            </div>
            <span className="mr-showing-label">
              Showing <strong>{filtered.length}</strong> of {totalReviews}
            </span>
          </div>
        </div>

        {/* REVIEWS GRID */}
        {loading ? (
          <div className="mr-empty">
            <div className="mr-spinner"></div>
            <p style={{ marginTop: 12, color: "#888" }}>Loading reviews...</p>
          </div>
        ) : filtered.length > 0 ? (
          <div className="mr-reviews-grid">
            {filtered.map(review => (
              <div key={review.id} className="mr-review-card">

                {/* Card Header */}
                <div className="mr-card-header">
                  <div className="mr-farmer-info">
                    <div className="mr-farmer-avatar">{(review.customerName || "C").charAt(0)}</div>
                    <div>
                      <div className="mr-farmer-name">{review.customerName}</div>
                      <div className="mr-farmer-city">{review.customerCity}</div>
                    </div>
                  </div>
                  <div className="mr-card-meta">
                    <RenderStars rating={review.rating} size={14} />
                    <span className="mr-card-date">{review.date}</span>
                  </div>
                </div>

                {/* Job Tag */}
                <div className="mr-job-tag">
                  <FaWrench style={{ color: "#FDB714", fontSize: 11 }} />
                  {review.machineryType}
                </div>

                {/* Review Text */}
                <div className="mr-review-body">
                  <FaQuoteLeft className="mr-quote-icon" />
                  <p className="mr-review-text">{review.review}</p>
                </div>

                {/* Card Footer */}
                

              </div>
            ))}
          </div>
        ) : (
          <div className="mr-empty">
            <FaStar style={{ fontSize: 40, color: "#ddd" }} />
            <h4>No reviews found</h4>
            <p>{totalReviews === 0 ? "No customers have left reviews yet." : "No reviews match this rating filter."}</p>
          </div>
        )}

      </div>
    </MechanicAdminLayout>
  );
}